import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiFilter, FiChevronDown, FiX, FiCheck } = FiIcons;

const SORT_OPTIONS = [
  { id: 'date_desc', label: 'Newest First' },
  { id: 'date_asc', label: 'Oldest First' },
  { id: 'glow_desc', label: 'Highest Glow' },
  { id: 'glow_asc', label: 'Lowest Glow' },
  { id: 'water_desc', label: 'Most Hydrated' },
];

const GLOW_RANGES = [
  { id: 'all', label: 'All Scores' },
  { id: 'high', label: '8–10 ✦ Glowing' },
  { id: 'mid', label: '5–7 ✦ Good' },
  { id: 'low', label: '1–4 ✦ Rough' },
];

const TAG_OPTIONS = ['Glowy', 'Smooth', 'Even tone', 'Dry', 'Puffy', 'Breakout', 'Acne', 'Inflamed'];

export default function TimelineFilters({ filters, onChange }) {
  const [open, setOpen] = useState(false);
  
  const activeCount = [
    filters.sort !== 'date_desc',
    filters.glowRange !== 'all',
    filters.tags.length > 0,
    filters.hasPhoto !== 'all',
    filters.hasTreatment !== 'all',
  ].filter(Boolean).length;

  const toggleTag = (tag) => {
    const next = filters.tags.includes(tag) 
      ? filters.tags.filter(t => t !== tag)
      : [...filters.tags, tag];
    onChange({ ...filters, tags: next });
  };

  const reset = () => {
    onChange({ sort: 'date_desc', glowRange: 'all', tags: [], hasPhoto: 'all', hasTreatment: 'all' });
  };

  return (
    <div className="mb-5">
      <div className="flex items-center gap-2">
        <button
          onClick={() => setOpen(o => !o)}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-sans font-medium transition-all ${
            open || activeCount > 0 
              ? 'bg-slate-900 text-white' 
              : 'glass-input text-slate-700'
          }`}
        >
          <SafeIcon icon={FiFilter} className="text-sm" />
          Filter
          {activeCount > 0 && (
            <span className="bg-white text-slate-900 text-xs font-bold w-4 h-4 rounded-full flex items-center justify-center">
              {activeCount}
            </span>
          )}
        </button>

        <div className="relative flex-1">
          <select
            value={filters.sort}
            onChange={e => onChange({ ...filters, sort: e.target.value })}
            className="w-full appearance-none glass-input rounded-full px-4 py-2.5 pr-8 text-sm font-sans text-slate-700 focus:outline-none"
          >
            {SORT_OPTIONS.map(o => (
              <option key={o.id} value={o.id}>{o.label}</option>
            ))}
          </select>
          <SafeIcon icon={FiChevronDown} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none" />
        </div>

        {activeCount > 0 && (
          <button
            onClick={reset}
            className="w-10 h-10 rounded-full glass-input flex items-center justify-center active:bg-white/50 transition-colors flex-shrink-0"
          >
            <SafeIcon icon={FiX} className="text-slate-500 text-sm" />
          </button>
        )}
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="glass-card rounded-[2rem] mt-3 p-5 space-y-5">
              <div>
                <p className="font-sans text-xs font-semibold text-slate-400 uppercase tracking-widest mb-3">Glow Score</p>
                <div className="flex flex-wrap gap-2">
                  {GLOW_RANGES.map(r => (
                    <button
                      key={r.id}
                      onClick={() => onChange({ ...filters, glowRange: r.id })}
                      className={`px-4 py-2 rounded-full text-xs font-sans font-medium transition-all ${
                        filters.glowRange === r.id 
                          ? 'bg-slate-900 text-white' 
                          : 'glass-input text-slate-600'
                      }`}
                    >
                      {filters.glowRange === r.id && <span className="mr-1">✓</span>}{r.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <p className="font-sans text-xs font-semibold text-slate-400 uppercase tracking-widest mb-3">Skin Tags</p>
                <div className="flex flex-wrap gap-2">
                  {TAG_OPTIONS.map(tag => (
                    <button
                      key={tag}
                      onClick={() => toggleTag(tag)}
                      className={`px-4 py-2 rounded-full text-xs font-sans font-medium transition-all ${
                        filters.tags.includes(tag) 
                          ? 'bg-[#e5e1f9]/80 text-[#5b21b6] border border-[#ddd6fe]' 
                          : 'glass-input text-slate-600'
                      }`}
                    >
                      {filters.tags.includes(tag) && '✓ '}{tag}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <p className="font-sans text-xs font-semibold text-slate-400 uppercase tracking-widest mb-3">Quick Filters</p>
                <div className="flex gap-2 flex-wrap">
                  {[
                    { key: 'hasPhoto', label: 'Has Photo', value: 'yes' },
                    { key: 'hasTreatment', label: 'Has Treatment', value: 'yes' },
                  ].map(({ key, label, value }) => (
                    <button
                      key={key}
                      onClick={() => onChange({ ...filters, [key]: filters[key] === value ? 'all' : value })}
                      className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-sans font-medium transition-all ${
                        filters[key] === value 
                          ? 'bg-[#d1ece5]/80 text-[#0f766e] border border-[#bbf7d0]' 
                          : 'glass-input text-slate-600'
                      }`}
                    >
                      {filters[key] === value && <SafeIcon icon={FiCheck} className="text-xs" />}
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <button
                onClick={() => setOpen(false)}
                className="w-full py-3.5 rounded-2xl bg-slate-900 text-white font-sans text-sm font-medium active:scale-[0.98] transition-transform"
              >
                Apply Filters
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}